/// REACT ///
import { useState } from "react";
/// UUID ///
import { v4 } from "uuid";
/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
/// ACTIONS ///
import { createRestorePoint } from "api/actions/history";
import { updateLocalShopSink } from "api/actions/shop_quote";
import { createShopSink } from "api/actions/shop_quote";
import { markShopQuoteChanged } from "api/actions/shop_quote";
/// TYPES ///
import { CutoutError } from "api/types/sketch";
import { IHistoryActionType } from "api/types/history";
import { IShopCounter } from "api/types/shop_quote/counter";
import { IShopSink } from "api/types/shop_quote/sink";
import { Vector2 } from "api/types/sketch";
/// COMPONENTS ///
import SaveSink from "components/quote/drawer/sink";
/// VALUES ///
import { UNDERMOUNT } from "values/values";
import { RECTANGLE } from "values/values";
/// EMPTY ///
import { empty_sink } from "values/empty/quote/sink";

interface Props {
  open: boolean,
  setOpen: (open: boolean) => void,
  selected_counter: IShopCounter,
  mouse: Vector2
}

export default function CreateShopSink({
  open,
  setOpen,
  selected_counter,
  mouse
}: Props) {
  const dispatch = useAppDispatch();
  const [sink, setSink] = useState<IShopSink>({ ...empty_sink });
  const [error, setError] = useState<CutoutError>({});

  const close = () => {
    setOpen(false);
    setError({});
    setSink({ ...empty_sink });
  }

  const save = () => {
    let error: CutoutError = {};
    if (!sink?.length) {
      error.length_error = "Length must be greater than 0";
      error.length_has_error = true;
    }
    if (!sink?.width) {
      error.width_error = "Width must be greater than 0";
      error.width_has_error = true;
    }

    if (error.length_has_error || error.width_has_error) {
      setError(error);
      return;
    }

    if (selected_counter) {
      const new_sink: IShopSink = {
        ...sink,
        counter_uuid: selected_counter?.uuid,
        uuid: v4(),
        angle: -90,
        x: Math.round(mouse.X),
        y: Math.round(mouse.Y),
        location: {
          X: Math.round(mouse.X),
          Y: Math.round(mouse.Y)
        }
      };

      dispatch(createShopSink(new_sink));
      dispatch(markShopQuoteChanged());

      dispatch(createRestorePoint(
        {
          undo_action: IHistoryActionType.DELETE_SHOP_SINK,
          redo_action: IHistoryActionType.CREATE_SHOP_SINK,
          desc: "Create Sink Cutout",
          undo_data: { uuid: new_sink.uuid, counter_uuid: new_sink.counter_uuid },
          redo_data: new_sink
        }
      ));
      close();
    }
  }

  const update = (updated_sink: IShopSink) => {
    setSink(updated_sink);
    dispatch(updateLocalShopSink(updated_sink));
  }

  return (
    <SaveSink
      open={open}
      setOpen={setOpen}
      sink_type={sink?.sink_type ?? UNDERMOUNT}
      setSinkType={sink_type => update({ ...sink, sink_type })}
      shape={sink?.shape ?? RECTANGLE}
      setShape={shape => update({ ...sink, shape })}
      faucet_hole_count={sink?.faucet_hole_count ?? 1}
      setFaucetHoleCount={faucet_hole_count => update({ ...sink, faucet_hole_count })}
      length={sink?.length ?? 30}
      setLength={length => update({ ...sink, length })}
      length_error={error?.length_error ?? ""}
      length_has_error={error?.length_has_error ?? false}
      width={sink?.width ?? 26}
      setWidth={width => update({ ...sink, width })}
      width_error={error?.width_error ?? ""}
      width_has_error={error?.width_has_error ?? false}
      save={save}
      cancel={close}
      title="Create Sink Cutout" />
  );
}

